import type { Axios } from 'axios'
import type {
  AuthPrecision,
  UserRole,
} from 'src/tpbp-core/models/auth/auth.types'
import {
  AUTH_PRECISIONS,
  USER_ROLES,
} from 'src/tpbp-core/models/auth/auth.types'
import type { ProxyRecord } from '../deserializers/deserializers.types'
interface JsonApiDefineOptions {
  serializer: (
    record: object,
  ) => { id: string; type: string; attributes: object } | undefined
  deserializer: (data: object, links?: unknown[]) => unknown
}

interface JsonApiAxios {
  get: <T = unknown>(
    url: string,
    config?: object,
  ) => Promise<JsonApiResponse<T>>
  post: <T = unknown>(
    url: string,
    data?: object,
    config?: object,
  ) => Promise<JsonApiResponse<T>>
  put: <T = unknown>(
    url: string,
    data?: object,
    config?: object,
  ) => Promise<JsonApiResponse<T>>
  interceptors: Axios['interceptors']
}

export interface JsonApiResponse<T = any, M = any, I = unknown> {
  data: T
  document?: {
    data: T
    included?: I
  }
  meta?: M
}

interface JsonApiOptions {
  [key: string]: string | number | boolean | object | undefined
  include?: string[] | string
}

interface JsonApiChain<T = any> {
  all: (objectType: string) => JsonApiChain<T>
  destroy: () => Promise<JsonApiResponse<T>>
  get: (options?: JsonApiOptions) => Promise<JsonApiResponse<T> | Error>
  post: (
    data: object,
    options?: JsonApiOptions,
  ) => Promise<JsonApiResponse<T> | Error>
}

type JsonApiMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

export interface JsonApiPayload {
  req: object
  jsonApi: object
  res?: object
}

type JsonApiMiddleware = {
  name: string
  req?: (payload: object) => Promise<object>
}

export type JsonApiParameters =
  | ({
      useTpbpPrefix?: boolean
    } & Record<string, unknown>)
  | null

export interface JsonApiInstance {
  _getBackEndErrors: () => unknown[]
  _lastErrors: unknown[] | null
  $serialize: (
    record: object,
  ) => { id: string; type: string; attributes: object } | undefined
  all: <T = any>(type: string) => JsonApiChain<T>
  apiUrl: string
  applyErrorMiddleware: (error: object) => Promise<object>
  applyRequestMiddleware: (
    request: object,
  ) => Promise<{ req: object; jsonApi: object; res?: object }>
  applyResponseMiddleware: (response: object) => Promise<object>
  axios: JsonApiAxios
  create: (
    type: string,
    data: object,
    parameters: JsonApiParameters,
  ) => Promise<JsonApiResponse>
  define: (type: string, model: unknown, options?: JsonApiDefineOptions) => void
  findAll: <T = any, W = any, I = unknown>(
    url: string,
  ) => Promise<JsonApiResponse<T, W, I>>
  headers: Record<string, string>
  insertMiddlewareBefore: (name: string, before: JsonApiMiddleware) => void
  one: <T = any>(type: string, id: string) => JsonApiChain<T>
  replaceMiddleware: (name: string, withMiddleware: JsonApiMiddleware) => void
  request: <T = any, M = unknown, I = unknown>(
    url: string,
    method: JsonApiMethod,
    parameters?: JsonApiParameters,
    data?: object,
  ) => Promise<JsonApiResponse<T, M, I> | Error>
  runMiddleware: (request: object) => Promise<object>
  update: (
    type: string,
    data: object,
    parameters: JsonApiParameters,
  ) => Promise<JsonApiResponse>
}

type JsonApiContextCallback = (response?: Response) => unknown

export interface JsonApiContext {
  modalOpened: boolean
  _on401Error: JsonApiContextCallback | null
  _onError: JsonApiContextCallback | null
}

export const API_USER_ROLES = {
  SELF_CARE: 'customer',
  ADVISOR: 'advisor',
  ADVISING_REPORT_PDF_GENERATOR: 'pdf',
  NONE: '',
} as const

export type ApiUserRole = ValueOf<typeof API_USER_ROLES>

export const TOKEN_PRECISIONS = {
  MUST_FOLLOW_ADVICE: 'advice_has_to_be_followed',
  USER_IS_MINOR: 'minor',
} as const
export type TokenPrecision = ValueOf<typeof TOKEN_PRECISIONS>

export const TOKEN_SET_PRECISIONS_TO_AUTH_PRECISION_MAPPER = {
  [TOKEN_PRECISIONS.MUST_FOLLOW_ADVICE]: AUTH_PRECISIONS.MUST_FOLLOW_ADVICE,
  [TOKEN_PRECISIONS.USER_IS_MINOR]: AUTH_PRECISIONS.USER_IS_MINOR,
} as const satisfies Record<TokenPrecision, AuthPrecision>

export const API_USER_KIND_TO_USER_ROLE_MAPPER = {
  [API_USER_ROLES.ADVISING_REPORT_PDF_GENERATOR]:
    USER_ROLES.ADVISING_REPORT_PDF_GENERATOR,
  [API_USER_ROLES.ADVISOR]: USER_ROLES.ADVISOR,
  [API_USER_ROLES.SELF_CARE]: USER_ROLES.SELF_CARE,
  [API_USER_ROLES.NONE]: USER_ROLES.NONE,
} as const satisfies Record<ApiUserRole, UserRole>

export interface TokenSet {
  access_token: string
  kind: string
  target_origin: string
  refresh_token: string
  precisions: TokenPrecision[]
}

export type QueryParameters = Record<
  string,
  string | number | boolean | undefined
>

export type JsonApiIncludedItems = {
  id: string
  type: string
}

export type RawJsonApiData<Model extends ProxyRecord> = {
  attributes: Omit<Model, keyof ProxyRecord>
} & Record<string, unknown>

export type JsonApiErrorResponseError = {
  title: string
  detail: string
}

export type JsonApiErrorResponse = {
  errors: JsonApiErrorResponseError[]
}
